import React, { useMemo, useRef } from 'react';

import { useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import FreecodeFlowOverlay from 'components/FreecodePage/FreecodeFlowOverlay';
import {
  ExerciseProgressContainer,
  HeaderIcon,
  ProgressHeader,
  LevelRow,
  LevelCircleContainer,
  LevelCircle,
  LevelLabel,
  ProgressLine,
} from './styles';

export default function ExerciseProgress({ nextButtonSettings }) {
  const { test, testImpl, input } = useFreecodeContextData();
  const { levelToRun, selectLevelToRun, language, updateAndRunFlow } = useFreecodeContextRun();

  const exerciseNameRef = useRef(null);

  // Array with bools for if input index has firstSuccess for language
  const languageCompletion = useMemo(
    () =>
      Array.from({ length: testImpl.configLevels.numPublic }, (_, i) => i).map(
        (level) => !!input?.levelData?.[String(level)]?.[language]?.firstSuccess
      ),
    [input?.levelData, language, testImpl.configLevels.numPublic]
  );

  const levelSettings = useMemo(() => {
    const checkLevelCompletionForFlow = (testImpl, levelIndex, language) => {
      language = language === 'any' ? 'python' : language;
      if (!testImpl?.parsedFlow?.[levelIndex]?.length) return false;
      return (
        input?.levelData?.[levelIndex]?.[language]?.flowProgress?.length === testImpl.parsedFlow?.[levelIndex]?.length
      );
    };

    const checkIsLevelCompleted = (testImpl, levelIndex, language, levelCompletedCondition) =>
      testImpl?.parsedFlow?.[levelIndex]?.[testImpl.parsedFlow[levelIndex].length - 1]?.includes('[coding]')
        ? checkLevelCompletionForFlow(testImpl, levelIndex, language) && levelCompletedCondition
        : checkLevelCompletionForFlow(testImpl, levelIndex, language);

    return [...Array(testImpl.configLevels.numPublic).keys()].map((levelIndex) => {
      let isCompleted = languageCompletion?.[levelIndex];
      if (testImpl.isFlow) isCompleted = checkIsLevelCompleted(testImpl, levelIndex, language, isCompleted);
      return {
        levelName: `Level ${levelIndex + 1} - ` + (testImpl.generated.allLevelNames?.[levelIndex] ?? 'unknown...'),
        checked: levelIndex === levelToRun,
        completed: isCompleted,
        showLine: levelIndex + 1 < testImpl.configLevels.numPublic || !!nextButtonSettings?.buttonText,
      };
    });
  }, [levelToRun, nextButtonSettings?.buttonText, languageCompletion, language, testImpl, input?.levelData]);

  const moreExercisesButton = () => {
    if (!nextButtonSettings.buttonText) return;
    return (
      <LevelRow
        isCurrent={false}
        title={nextButtonSettings.title}
        onClick={() => nextButtonSettings.setDisplayNextModal(true)}
      >
        <LevelCircleContainer>
          <LevelCircle />
        </LevelCircleContainer>
        <LevelLabel>{nextButtonSettings.buttonText}</LevelLabel>
      </LevelRow>
    );
  };

  const renderLevelRow = (levelSettings, index) => {
    return (
      <LevelRow
        key={index}
        isCurrent={levelSettings.checked}
        title={`Select level ${index + 1}`}
        onClick={() => {
          selectLevelToRun(index);
          updateAndRunFlow(index);
        }}
      >
        <LevelCircleContainer>
          <LevelCircle checked={levelSettings.checked} completed={levelSettings.completed} />
        </LevelCircleContainer>
        <LevelLabel>{levelSettings.levelName}</LevelLabel>
        {levelSettings.showLine && <ProgressLine />}
      </LevelRow>
    );
  };

  return (
    <ExerciseProgressContainer>
      <FreecodeFlowOverlay animationAnchorRef={exerciseNameRef} />
      <ProgressHeader ref={exerciseNameRef}>
        <HeaderIcon />
        {test.name}
      </ProgressHeader>
      {levelSettings.map(renderLevelRow)}
      {moreExercisesButton()}
    </ExerciseProgressContainer>
  );
}
